/**
 * QBridgeSetupPage - Institutional Grade Setup Experience
 *
 * Full-page setup wizard for QBridge desktop application
 * Route: /qbridge/setup (opened automatically after QBridge installation)
 *
 * Flow:
 * Step 0: Welcome & Terms Acceptance (NEW)
 * Step 1: Detect QBridge + MT5
 * Step 2: Sign In / Authenticate
 * Step 3: Import Trades
 * Step 4: Success
 *
 * Design: Institutional grade, professional, trust-building
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepConnector,
    IconButton,
    Tooltip,
    Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Step Components
import WelcomeTermsStep from '../../Components/QAI/QBridgeWizard/steps/WelcomeTermsStep';
import DetectStepEnhanced from '../../Components/QAI/QBridgeWizard/steps/DetectStepEnhanced';
import AuthStep from '../../Components/QAI/QBridgeWizard/steps/AuthStep';
import ImportStep from '../../Components/QAI/QBridgeWizard/steps/ImportStep';
import SuccessStep from '../../Components/QAI/QBridgeWizard/steps/SuccessStep';

// Hooks
import useQBridgeStatus from '../../hooks/useQBridgeStatus';
import useMT5Detection from '../../hooks/useMT5Detection';

// API
import { checkImportStatus } from '../../api/qaiApi';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS - Institutional Grade
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
    // Primary Colors
    primary: '#27B56D',
    primaryLight: '#22C55E',
    primaryDark: '#16A34A',
    primaryGlow: 'rgba(39, 181, 109, 0.4)',
    primaryBg: 'rgba(39, 181, 109, 0.1)',
    primaryBorder: 'rgba(39, 181, 109, 0.3)',

    // Accent Colors
    accent: '#3B82F6',
    accentLight: 'rgba(59, 130, 246, 0.1)',
    purple: '#7c3aed',

    // Background & Surface
    background: '#0A0A0F',
    surface: '#12121A',
    surfaceAlt: '#0D0D14',
    surfaceHover: 'rgba(255, 255, 255, 0.04)',

    // Text Hierarchy
    text: '#FFFFFF',
    textSecondary: '#E2E8F0',
    textMuted: '#94A3B8',
    textDim: '#64748B',

    // Borders
    border: 'rgba(255, 255, 255, 0.08)',
    borderLight: 'rgba(255, 255, 255, 0.12)',

    // Status Colors
    success: '#22C55E',
    warning: '#FFA500',
    warningBg: 'rgba(255, 165, 0, 0.1)',
    error: '#EF4444',
    errorBg: 'rgba(239, 68, 68, 0.1)',
    info: '#3B82F6',
    infoBg: 'rgba(59, 130, 246, 0.1)'
};

// ═══════════════════════════════════════════════════════════════════════════════
// STEP CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

const STEPS = [
    { label: 'Terms', description: 'Accept Terms' },
    { label: 'Connect', description: 'QBridge & MT5' },
    { label: 'Sign In', description: 'Link Account' },
    { label: 'Import', description: 'Sync Trades' },
    { label: 'Done', description: 'Complete' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// Custom Step Connector with gradient
const QStepConnector = styled(StepConnector)(() => ({
    '&.MuiStepConnector-alternativeLabel': {
        top: 18,
        left: 'calc(-50% + 24px)',
        right: 'calc(50% + 24px)'
    },
    '& .MuiStepConnector-line': {
        height: 3,
        border: 0,
        backgroundColor: BRAND.border,
        borderRadius: 2
    },
    '&.Mui-active .MuiStepConnector-line': {
        background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`
    },
    '&.Mui-completed .MuiStepConnector-line': {
        background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`
    }
}));

// Custom Step Icon
const QStepIcon = ({ active, completed, icon }) => {
    const baseStyles = {
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.9rem',
        fontWeight: 600,
        transition: 'all 0.3s ease',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    };

    if (completed) {
        return (
            <Box sx={{
                ...baseStyles,
                background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                color: '#FFFFFF',
                boxShadow: `0 0 20px ${BRAND.primaryGlow}`
            }}>
                <CheckCircleIcon sx={{ fontSize: '1.4rem' }} />
            </Box>
        );
    }

    if (active) {
        return (
            <Box sx={{
                ...baseStyles,
                background: BRAND.primaryBg,
                border: `2px solid ${BRAND.primary}`,
                color: BRAND.primary,
                boxShadow: `0 0 16px ${BRAND.primaryGlow}`
            }}>
                {icon}
            </Box>
        );
    }

    return (
        <Box sx={{
            ...baseStyles,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            color: BRAND.textDim
        }}>
            {icon}
        </Box>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const QBridgeSetupPage = () => {
    const navigate = useNavigate();

    // ─────────────────────────────────────────────────────────────────────────
    // STATE
    // ─────────────────────────────────────────────────────────────────────────
    const [currentStep, setCurrentStep] = useState(0);
    const [isInitializing, setIsInitializing] = useState(true);
    const [existingImportData, setExistingImportData] = useState(null);

    const [wizardState, setWizardState] = useState({
        termsAccepted: false,
        qbridgeDetected: false,
        mt5Detected: false,
        mt5Account: null,
        mt5Broker: null,
        mt5Balance: null,
        mt5Currency: null,
        authorized: false,
        userEmail: null,
        importComplete: false,
        importedCount: 0,
        skippedCount: 0,
        tokensUsed: 0
    });

    // ─────────────────────────────────────────────────────────────────────────
    // HOOKS - QBridge & MT5 Detection
    // ─────────────────────────────────────────────────────────────────────────

    // Poll QBridge status when on step 1 (Connect)
    const {
        status: qbridgeStatus,
        isRunning: qbridgeRunning,
        version: qbridgeVersion
    } = useQBridgeStatus(currentStep === 1);

    // Poll MT5 detection when QBridge is running and on step 1
    const {
        detected: mt5Detected,
        connected: mt5Connected,
        account: mt5Account,
        broker: mt5Broker,
        balance: mt5Balance,
        currency: mt5Currency,
        isSearching: mt5Searching
    } = useMT5Detection(qbridgeRunning && currentStep === 1);

    // ─────────────────────────────────────────────────────────────────────────
    // HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    const getToken = useCallback(() => {
        return localStorage.getItem('qai_token');
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // INITIALIZATION
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const initializeWizard = async () => {
            setIsInitializing(true);
            const token = getToken();

            try {
                // Check if user has existing imports
                if (token) {
                    const importStatus = await checkImportStatus(token);
                    setExistingImportData(importStatus);

                    if (importStatus.hasExistingImport && importStatus.tradeCount > 0) {
                        console.log('[QBridgeSetupPage] Existing import found:', importStatus.tradeCount, 'trades');
                    }
                }
            } catch (error) {
                console.warn('[QBridgeSetupPage] Failed to check import status:', error);
            }

            setIsInitializing(false);
        };

        initializeWizard();
    }, [getToken]);

    // ─────────────────────────────────────────────────────────────────────────
    // AUTO-ADVANCE EFFECTS
    // ─────────────────────────────────────────────────────────────────────────

    // Update state when QBridge is detected
    useEffect(() => {
        if (qbridgeRunning && currentStep === 1) {
            setWizardState(prev => ({ ...prev, qbridgeDetected: true }));
        }
    }, [qbridgeRunning, currentStep]);

    // Update state when MT5 is detected
    useEffect(() => {
        if (mt5Connected && currentStep === 1) {
            setWizardState(prev => ({
                ...prev,
                mt5Detected: true,
                mt5Account,
                mt5Broker,
                mt5Balance,
                mt5Currency
            }));
        }
    }, [mt5Connected, mt5Account, mt5Broker, mt5Balance, mt5Currency, currentStep]);

    // ─────────────────────────────────────────────────────────────────────────
    // NAVIGATION HANDLERS
    // ─────────────────────────────────────────────────────────────────────────

    const goToStep = (step) => {
        if (step >= 0 && step < STEPS.length) {
            setCurrentStep(step);
        }
    };

    const goNext = () => goToStep(currentStep + 1);
    const goBack = () => goToStep(currentStep - 1);

    // Step-specific handlers
    const handleTermsAccepted = () => {
        setWizardState(prev => ({ ...prev, termsAccepted: true }));
        goNext();
    };

    const handleConnectionComplete = () => {
        goNext();
    };

    const handleAuthComplete = (email) => {
        setWizardState(prev => ({ ...prev, authorized: true, userEmail: email }));
        goNext();
    };

    const handleImportComplete = (result) => {
        setWizardState(prev => ({
            ...prev,
            importComplete: true,
            importedCount: result.imported || 0,
            skippedCount: result.skipped || 0,
            tokensUsed: result.tokensUsed || 0
        }));
        goNext();
    };

    const handleOpenCoach = () => {
        navigate('/q-ai/coach');
    };

    const handleClose = () => {
        // Just close the window/tab since user came from installer
        window.close();
        // Fallback if window.close doesn't work
        navigate('/q-ai');
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: BRAND.background,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* ═══════════════════════════════════════════════════════════════════
                HEADER
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 2, md: 4 },
                    py: 2,
                    borderBottom: `1px solid ${BRAND.border}`
                }}
            >
                {/* Logo & Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Q Logo */}
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 0 20px ${BRAND.primaryGlow}`
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#FFFFFF',
                                fontSize: '1.25rem',
                                fontWeight: 800,
                                fontFamily: "'Inter', sans-serif"
                            }}
                        >
                            Q
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            color: BRAND.text,
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                        }}
                    >
                        QBridge Setup
                    </Typography>
                </Box>

                {/* Help Button */}
                <Tooltip title="Need help? Contact support">
                    <IconButton
                        sx={{
                            color: BRAND.textMuted,
                            '&:hover': {
                                color: BRAND.text,
                                backgroundColor: BRAND.surfaceHover
                            }
                        }}
                        onClick={() => window.open('https://discord.gg/quantx', '_blank')}
                    >
                        <HelpOutlineIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                MAIN CONTENT
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    maxWidth: '800px',
                    mx: 'auto',
                    width: '100%'
                }}
            >
                {/* ─────────────────────────────────────────────────────────────────
                    STEPPER
                    ───────────────────────────────────────────────────────────────── */}
                <Box sx={{ width: '100%', mb: 4 }}>
                    <Stepper
                        activeStep={currentStep}
                        alternativeLabel
                        connector={<QStepConnector />}
                    >
                        {STEPS.map((step, index) => (
                            <Step key={step.label} completed={index < currentStep}>
                                <StepLabel
                                    StepIconComponent={(props) => (
                                        <QStepIcon {...props} icon={index + 1} />
                                    )}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            sx={{
                                                color: index === currentStep
                                                    ? BRAND.text
                                                    : index < currentStep
                                                        ? BRAND.primary
                                                        : BRAND.textDim,
                                                fontSize: '0.8125rem',
                                                fontWeight: index === currentStep ? 600 : 400,
                                                fontFamily: "'Inter', sans-serif",
                                                transition: 'color 0.2s ease'
                                            }}
                                        >
                                            {step.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: BRAND.textDim,
                                                fontSize: '0.75rem',
                                                mt: 0.25,
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* ─────────────────────────────────────────────────────────────────
                    STEP CONTENT
                    ───────────────────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        width: '100%',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Step 0: Welcome & Terms */}
                    <Fade in={currentStep === 0} mountOnEnter unmountOnExit>
                        <Box sx={{ flex: 1 }}>
                            <WelcomeTermsStep
                                onAccept={handleTermsAccepted}
                                colors={BRAND}
                            />
                        </Box>
                    </Fade>

                    {/* Step 1: Connect QBridge & MT5 */}
                    <Fade in={currentStep === 1} mountOnEnter unmountOnExit>
                        <Box sx={{ flex: 1 }}>
                            <DetectStepEnhanced
                                qbridgeRunning={qbridgeRunning}
                                qbridgeVersion={qbridgeVersion}
                                mt5Detected={mt5Connected}
                                mt5Account={mt5Account}
                                mt5Broker={mt5Broker}
                                mt5Balance={mt5Balance}
                                mt5Currency={mt5Currency}
                                isSearching={mt5Searching}
                                onNext={handleConnectionComplete}
                                onBack={goBack}
                                colors={BRAND}
                            />
                        </Box>
                    </Fade>

                    {/* Step 2: Sign In */}
                    <Fade in={currentStep === 2} mountOnEnter unmountOnExit>
                        <Box sx={{ flex: 1 }}>
                            <AuthStep
                                getToken={getToken}
                                mt5Account={wizardState.mt5Account}
                                mt5Broker={wizardState.mt5Broker}
                                onComplete={handleAuthComplete}
                                onBack={goBack}
                                colors={BRAND}
                            />
                        </Box>
                    </Fade>

                    {/* Step 3: Import Trades */}
                    <Fade in={currentStep === 3} mountOnEnter unmountOnExit>
                        <Box sx={{ flex: 1 }}>
                            <ImportStep
                                onComplete={handleImportComplete}
                                onBack={goBack}
                                onClose={handleClose}
                                colors={BRAND}
                                existingImportData={existingImportData}
                            />
                        </Box>
                    </Fade>

                    {/* Step 4: Success */}
                    <Fade in={currentStep === 4} mountOnEnter unmountOnExit>
                        <Box sx={{ flex: 1 }}>
                            <SuccessStep
                                importedCount={wizardState.importedCount}
                                skippedCount={wizardState.skippedCount}
                                tokensUsed={wizardState.tokensUsed}
                                mt5Account={wizardState.mt5Account}
                                mt5Broker={wizardState.mt5Broker}
                                onOpenCoach={handleOpenCoach}
                                onClose={handleClose}
                                colors={BRAND}
                            />
                        </Box>
                    </Fade>
                </Box>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                FOOTER
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: { xs: 2, md: 4 },
                    py: 2,
                    borderTop: `1px solid ${BRAND.border}`
                }}
            >
                <Typography
                    sx={{
                        color: BRAND.textDim,
                        fontSize: '0.75rem',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    QBridge v1.0.0
                </Typography>
                <Typography sx={{ color: BRAND.textDim, fontSize: '0.75rem' }}>•</Typography>
                <Typography
                    sx={{
                        color: BRAND.textDim,
                        fontSize: '0.75rem',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    © 2025 QTradeView. All rights reserved.
                </Typography>
            </Box>
        </Box>
    );
};

export default QBridgeSetupPage;
